import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Button, Radio, Switch } from 'antd';
import classnames from 'classnames';
import { Destination } from '../../services/exports/Constants';
import { FAKE_ORDER } from '../../services/exports/FakeData';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import ProfileManager from '../../services/api/ProfileManager';
import { useDeletePrinterMutation, useUpdatePrinterMutation } from '../../services/api/PrinterManager';
import WebViewCommunicatorResource from '../../services/resources/WebViewCommunicatorResource';
import usePrinter from '../../hooks/usePrinter';
import usePrinters from '../../hooks/usePrinters';
import { ReceiptType } from '../../providers/PrintProvider';
import Layout from '../../components/layout/Layout';
import ConfirmationModal from '../../modals/ConfirmationModal';
import CopiesStepper from '../../components/print/CopiesStepper';
import PrinterRow from '../../components/print/PrinterRow';
import PrinterStatusAlert, { printerStatusLabelKey } from '../../components/print/PrinterStatusAlert';

const ConnectTimeout = 30_000;

export default function PrinterSettingsView() {
  const { t } = useTranslation(null, { keyPrefix: 'Views:PrinterSettings:PrinterSettingsView' });

  const { print, printing } = usePrinter();
  const { defaultPrinter, externalPrinterEnabled, printers, printsExternally } = usePrinters();

  const [updatePrinter, { isLoading: updating }] = useUpdatePrinterMutation();
  const [deletePrinter, { isLoading: deleting }] = useDeletePrinterMutation();

  const saving = updating || deleting;

  const { company } = useSelector((state) => state.company);
  const { print_copies, auto_print } = company;
  const { externalPrinterConnected, externalPrinterReports, externalPrinterStatus } = useSelector(
    (state) => state.print,
  );

  const [connecting, setConnecting] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const connectTimer = useRef(null);

  const externalPrinter = useMemo(() => defaultPrinter ?? printers?.[0], [defaultPrinter, printers]);

  // Position of the control reflects the connected external printer's default flag. A printer
  // paired from a newer device, or while the company still had the feature, can be the default
  // where it cannot be used, which would otherwise leave the group sitting on a hidden row.
  const destination = externalPrinterEnabled && externalPrinter?.is_default ? Destination.External : Destination.Sunmi;

  const externalName = externalPrinter?.display_name ?? externalPrinter?.name ?? t('printers.external.title');
  const paperWidthMm = externalPrinter?.settings?.paper_width_mm;

  const statusLabelKey = printerStatusLabelKey({
    status: externalPrinterStatus,
    connecting,
    connected: externalPrinterConnected,
  });

  const externalSubtitle = useMemo(() => {
    if (!externalPrinter) {
      return t('statuses.disconnected.label');
    }

    return [externalName, paperWidthMm && `${paperWidthMm} mm`, statusLabelKey && t(statusLabelKey)]
      .filter(Boolean)
      .join(' · ');
  }, [externalPrinter, externalName, paperWidthMm, statusLabelKey, t]);

  // Values arrive already persisted from redux-persist, so a plain mount effect would
  // re-save them on every visit to this screen. Only push a real change.
  const persisted = useRef({ auto_print, print_copies });

  useEffect(() => {
    if (persisted.current.auto_print === auto_print && persisted.current.print_copies === print_copies) {
      return;
    }

    persisted.current = { auto_print, print_copies };

    ProfileManager.update({
      ...company,
      auto_print,
      print_copies,
    });
  }, [print_copies, auto_print]);

  useEffect(() => {
    setConnecting(false);
    clearTimeout(connectTimer.current);
  }, [externalPrinterReports]);

  useEffect(() => () => clearTimeout(connectTimer.current), []);

  // The printer can also disappear from under an open confirmation — the native app owns
  // pairings too, and PrintersUpdatedHandler refetches the list behind the portal's back.
  // Confirming then hits the `!externalPrinter` guard and does nothing at all, so drop the
  // question. Clearing the flag rather than hiding on it also means a printer paired later
  // cannot inherit a stale confirmation.
  useEffect(() => {
    if (!externalPrinter) {
      setConfirmingDisconnect(false);
    }
  }, [externalPrinter]);

  function toggleAutoPrint() {
    ReduxHooks.dispatch(actionCreators.company.toggleAutoPrint());
  }

  function saveNumberOfCopies(value) {
    ReduxHooks.dispatch(actionCreators.company.setPrintCopies(value));
  }

  function reportFailure({ error }) {
    if (error) {
      toast.error(t('toasts.failed_to_save_changes'));
    }
  }

  async function saveDestination(value) {
    // `destination` only flips once the invalidated list has been refetched, so without the
    // in-flight check a double-tap on the card fires the same PUT twice.
    if (saving || !externalPrinter || value === destination) {
      return;
    }

    const result = await updatePrinter({
      ...externalPrinter,
      is_default: value === Destination.External,
    });

    reportFailure(result);

    if (result.error) {
      return;
    }

    if (value === Destination.External) {
      retryConnection();

      return;
    }

    abandonConnectionAttempt();
  }

  async function disconnectExternalPrinter() {
    if (saving || !externalPrinter) {
      return;
    }

    const result = await deletePrinter(externalPrinter);

    reportFailure(result);

    if (!result.error) {
      setConfirmingDisconnect(false);
      abandonConnectionAttempt();
    }
  }

  // The native setup screen is the only place an external printer can be paired or
  // configured, so both connecting and reconfiguring hop over to it.
  function openExternalPrinterSetup() {
    WebViewCommunicatorResource.navigate({ screen: 'ExternalPrinterSetupScreen' });
  }

  function retryConnection() {
    setConnecting(true);
    WebViewCommunicatorResource.connectPrinter({ destination: Destination.External });

    clearTimeout(connectTimer.current);
    connectTimer.current = setTimeout(() => setConnecting(false), ConnectTimeout);
  }

  // A pending attempt belongs to the printer the user has just moved away from. Without this,
  // the alert — which now also renders on `connecting` — keeps describing a destination that
  // is no longer selected, or one that has just been unpaired, until native answers or the
  // 30s fallback fires.
  function abandonConnectionAttempt() {
    setConnecting(false);
    clearTimeout(connectTimer.current);
  }

  function testPrint() {
    print({ type: ReceiptType.Order, payload: FAKE_ORDER });
  }

  const externalSelected = destination === Destination.External;
  const externalUsable = Boolean(externalPrinter) && externalPrinterConnected;

  return (
    <Layout.Page className="space-y-lg max-w-[680px]">
      {externalPrinterEnabled && (externalSelected || connecting) && (
        <PrinterStatusAlert
          status={externalPrinterStatus}
          connecting={connecting}
          connected={externalPrinterConnected}
          printerName={externalName}
          autoPrint={auto_print}
          busy={saving}
          onRetry={retryConnection}
          onConfigure={openExternalPrinterSetup}
        />
      )}

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-xs">{t('sections.destination')}</p>
        <Radio.Group
          value={destination}
          onChange={(event) => saveDestination(event.target.value)}
          className="block space-y-sm"
        >
          <PrinterRow
            value={Destination.Sunmi}
            selected={!externalSelected}
            title={t('printers.inner.title')}
            canTest
            testing={printing}
            testDisabled={printsExternally || saving}
            onSelect={() => saveDestination(Destination.Sunmi)}
            onTestPrint={testPrint}
          />
          {externalPrinterEnabled && (
            <PrinterRow
              value={Destination.External}
              selected={externalSelected}
              disabled={!externalPrinter}
              title={t('printers.external.title')}
              subtitle={externalSubtitle}
              widthUnset={Boolean(externalPrinter) && !paperWidthMm}
              canTest={externalUsable}
              testing={printing}
              testDisabled={!printsExternally || saving}
              onSelect={() => saveDestination(Destination.External)}
              onTestPrint={testPrint}
              actions={
                <>
                  {externalPrinter ? (
                    <Button disabled={saving} onClick={openExternalPrinterSetup}>
                      {t('buttons.configure_external')}
                    </Button>
                  ) : (
                    <Button onClick={openExternalPrinterSetup}>{t('buttons.connect_external')}</Button>
                  )}
                  {externalPrinter && (
                    <Button disabled={saving} danger onClick={() => setConfirmingDisconnect(true)}>
                      {t('buttons.disconnect_external')}
                    </Button>
                  )}
                </>
              }
              footer={
                !externalPrinter && <p className="mt-sm text-sm text-gray-500">{t('printers.external.connect_hint')}</p>
              }
            />
          )}
        </Radio.Group>
      </div>

      <div className="border-t border-gray-200 pt-lg space-y-md">
        <div className="flex items-start justify-between gap-lg">
          <p className="font-medium">{t('form.auto_print.label')}</p>
          <Switch
            checked={auto_print}
            onChange={toggleAutoPrint}
            aria-label={t('form.auto_print.label')}
            className="shrink-0"
          />
        </div>

        {auto_print && externalSelected && !printsExternally && (
          <Alert type="warning" showIcon title={t('form.auto_print.not_ready')} />
        )}

        <div className="flex items-center justify-between gap-lg">
          <div>
            <p className={classnames('font-medium', { 'text-gray-500': !auto_print })}>
              {t('form.print_copies.label')}
            </p>
            {!auto_print && <p className="text-sm text-gray-500 mt-nano">{t('form.print_copies.hint')}</p>}
          </div>
          <CopiesStepper value={print_copies} onChange={saveNumberOfCopies} />
        </div>
      </div>

      {/* A centred modal rather than a Popconfirm on the button: the portal runs in a narrow
          POS WebView, where an anchored popover next to a wide button gets cramped and its
          small ok/cancel buttons miss the app's touch-sized controls. */}
      <ConfirmationModal
        show={confirmingDisconnect}
        title={t('printers.external.disconnect_confirm')}
        description={t('printers.external.disconnect_confirm_description')}
        confirmationButtonText={t('printers.external.disconnect_confirm_ok')}
        cancelButtonText={t('buttons.cancel')}
        onConfirm={disconnectExternalPrinter}
        // Cancel, Esc, the X and the mask all land here. Dismissing mid-DELETE would not stop
        // the unpair, so the modal holds until the request answers.
        onCancel={() => !deleting && setConfirmingDisconnect(false)}
        confirming={deleting}
      />
    </Layout.Page>
  );
}
